import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { MessageCircle, Send } from "lucide-react";
import { StatusIndicator } from "./StatusIndicator";

const WA_URL = "https://wa.me/855964453740";
const TG_URL = "https://t.me/tpusatcuan";

const lines = [
    { text: "PUSATCUAN:", gold: true },
    { text: "Bantuan WhatsApp", gold: false },
    { text: "& Telegram 24 Jam", gold: false },
];

const lineVariants = {
    hidden: { y: "110%" },
    visible: (i) => ({
        y: "0%",
        transition: { delay: 0.35 + i * 0.16, duration: 1.1, ease: [0.22, 1, 0.36, 1] },
    }),
};

export const Hero = () => {
    const ref = useRef(null);
    const reduceMotion = useReducedMotion();
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
    const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

    return (
        <section
            ref={ref}
            data-testid="hero-section"
            className="relative flex min-h-screen flex-col justify-center overflow-hidden px-6 pt-28 pb-16 lg:px-10"
        >
            <motion.div
                aria-hidden="true"
                style={reduceMotion ? undefined : { y: bgY }}
                className="absolute inset-0 -z-10"
            >
                <img
                    src="https://images.pexels.com/photos/31650383/pexels-photo-31650383.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                    alt=""
                    loading="eager"
                    fetchPriority="high"
                    width="940"
                    height="650"
                    className="h-[120%] w-full object-cover opacity-40"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/80 via-[#0A0A0A]/70 to-[#0A0A0A]" />
                <div className="absolute left-1/2 top-1/3 h-[420px] w-[420px] -translate-x-1/2 rounded-full bg-[#D4AF37]/10 blur-[140px]" />
            </motion.div>

            <motion.div style={reduceMotion ? undefined : { opacity: fade }} className="mx-auto w-full max-w-7xl">
                <h1
                    data-testid="hero-title"
                    className="font-serif-display text-4xl font-medium leading-[1.05] tracking-tight text-white sm:text-6xl lg:text-7xl"
                >
                    {lines.map((line, i) => (
                        <span key={i} className="block overflow-hidden pb-1">
                            <motion.span
                                custom={i}
                                variants={lineVariants}
                                initial="hidden"
                                animate="visible"
                                className={`block ${line.gold ? "gold-text" : ""}`}
                            >
                                {line.text}
                            </motion.span>
                        </span>
                    ))}
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.95, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    className="mt-8 max-w-xl text-base leading-relaxed text-[#A1A1AA] md:text-lg"
                >
                    Butuh bantuan cepat? Hubungi Customer Service PUSATCUAN 24 jam
                    nonstop via WhatsApp dan Telegram resmi. Layanan responsif, aman,
                    dan siap melayani kendala Anda kapan saja.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.15, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    className="mt-10 flex flex-col gap-4 sm:flex-row"
                >
                    <a
                        href={WA_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="hero-whatsapp-button"
                        className="group inline-flex items-center justify-center gap-3 rounded-full bg-gradient-to-r from-[#2ee06f] via-[#25D366] to-[#128C7E] px-8 py-4 text-sm font-semibold tracking-wide text-black transition-transform duration-300 hover:scale-[1.03] focus:outline-none focus:ring-1 focus:ring-[#25D366]"
                    >
                        <MessageCircle className="h-5 w-5 transition-transform duration-300 group-hover:-rotate-12" />
                        Chat WhatsApp Pusatcuan
                    </a>
                    <a
                        href={TG_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="hero-telegram-button"
                        className="group inline-flex items-center justify-center gap-3 rounded-full bg-gradient-to-r from-[#54b8e8] via-[#2AABEE] to-[#1d86c7] px-8 py-4 text-sm font-semibold tracking-wide text-black transition-transform duration-300 hover:scale-[1.03] focus:outline-none focus:ring-1 focus:ring-[#2AABEE]"
                    >
                        <Send className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-0.5" />
                        Chat Telegram Admin
                    </a>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.4, duration: 1 }}
                    className="mt-10 sm:hidden"
                >
                    <StatusIndicator />
                </motion.div>
            </motion.div>
        </section>
    );
};
